import React from "react";
import { Box, Divider, Grid, Typography } from "@mui/material";
import SimpleDataRender from "../components/SimpleDataRender/SimpleDataRender";
import { Shirt } from "../entities/Shirt";
import { ShirtModel } from "../entities/ShirtModel";
import {
  Brand,
  Competition,
  Condition,
  Player,
  Property,
  Season,
  ShirtType,
  Team,
  Variant,
} from "./shirts/types";
import { Dictionary } from "../types/types";
import { isExtraDataMatch, isExtraDataValue } from "./shirts/functions";
import FormatService from "./formatService";
import MatchDataRender from "../components/MatchDataRender/MatchDataRender";
import { Actions } from "../permission/constants";
import { AbilityContext } from "../permission/Can";

export default function RenderService() {
  const {
    booleanFormat,
    purchaseCostFormat,
    showDateFormat,
    capitalizeFirstLetter,
  } = FormatService();

  const ability = React.useContext(AbilityContext);

  const labels: Dictionary<string> = {
    shirtType: "Type",
    brand: "Brand",
    competition: "Competition",
    player: "Player",
    season: "Season",
    team: "Team",
    variant: "Variant",
    condition: "Condition",
    number: "Number",
    text: "Text",
    signature: "Signature",
    title: "Title",
    description: "Description",
    purchase_date: "Purchase Date",
    purchase_price: "Purchase Price",
    certificate: "Certificate",
    "match-worn": "Match Worn",
    "match-issued": "Match Issued",
  };

  const RenderShirtType = (shirtType: ShirtType) => {
    return (
      <SimpleDataRender label={labels["shirtType"]} value={shirtType.name} />
    );
  };
  const RenderBrand = (brand: Brand) => {
    return (
      <SimpleDataRender
        label={labels["brand"]}
        value={capitalizeFirstLetter(brand.name)}
      />
    );
  };
  const RenderCompetition = (competition: Competition) => {
    return (
      <SimpleDataRender
        label={labels["competition"]}
        value={competition.name}
      />
    );
  };
  const RenderPlayer = (player: Player | undefined) => {
    let renderData = <></>;
    if (player) {
      const value =
        player.alias && player.alias !== player.name
          ? player.name + ' "' + player.alias + '"'
          : player.name;
      renderData = <SimpleDataRender label={labels["player"]} value={value} />;
    }
    return renderData;
  };
  const RenderSeason = (season: Season) => {
    return (
      <SimpleDataRender
        label={labels["season"]}
        value={season.start_year + "/" + season.end_year}
      />
    );
  };
  const RenderTeam = (team: Team) => {
    return <SimpleDataRender label={labels["team"]} value={team.name} />;
  };
  const RenderVariant = (variant: Variant) => {
    return <SimpleDataRender label={labels["variant"]} value={variant.name} />;
  };

  const RenderCondition = (condition: Condition) => {
    return (
      <SimpleDataRender label={labels["condition"]} value={condition.name} />
    );
  };

  const RenderShirtModel = (shirtModel: ShirtModel) => {
    let renderData: Array<any> = [];

    renderData.push(RenderCompetition(shirtModel.competition));
    renderData.push(RenderTeam(shirtModel.team));
    renderData.push(RenderSeason(shirtModel.season));
    renderData.push(RenderPlayer(shirtModel.player));
    renderData.push(
      <SimpleDataRender label={labels["number"]} value={shirtModel.number} />
    );
    renderData.push(RenderBrand(shirtModel.brand));
    renderData.push(RenderShirtType(shirtModel.type));
    //renderData.push(RenderVariant(shirtModel.variant));

    return renderData;
  };
  const RenderSignature = (shirt: Shirt) => {
    let renderData;
    if (shirt.signature) {
      renderData = (
        <SimpleDataRender
          label={labels["signature"]}
          value={shirt.signature.description}
        />
      );
    } else {
      renderData = (
        <SimpleDataRender label={labels["signature"]} value={"No"} />
      );
    }
    return renderData;
  };

  // const RenderPropertyExtraData = (data: any) => {
  //   let renderData = <></>;

  //   if (data) {
  //     if (isExtraDataMatch(data)) {
  //       renderData = <MatchDataRender match={data.match} />;
  //     } else {
  //       if (isExtraDataValue(data)) {
  //         const valueData: any = data;
  //         renderData = (
  //           <Box sx={{ marginBottom: "1rem" }}>
  //             <Typography variant={"shirtViewText"}>
  //               {valueData.value}
  //             </Typography>
  //           </Box>
  //         );
  //       } else {
  //         renderData = data;
  //       }
  //     }
  //   }

  //   return renderData;
  // };

  const RenderProperty = (property: Property) => {
    return (
      <Box sx={{ marginBottom: "1.5rem" }}>
        <Box
          sx={{
            paddingBottom: "5px",
            borderBottom: "1px solid #ccc",
            marginBottom: "1rem",
          }}
        >
          <Typography
            variant={"shirtViewText"}
            sx={{ fontWeight: "600 !important", color: "#6f7d95 !important" }}
          >
            {labels[property.key]}
          </Typography>
        </Box>

        {/* {RenderPropertyExtraData(property.extra_data)} */}
      </Box>
    );
  };

  const RenderProperties = (properties: Array<Property>) => {
    let renderData: Array<any> = [];

    properties.forEach((p) => {
      renderData.push(RenderProperty(p));
    });

    return renderData;
  };

  const RenderShirt = (shirt: Shirt) => {
    let renderData: Array<any> = [];

    renderData.push(
      <Box>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: { xs: "1.2rem !important", md: "1.2rem !important" },
            marginTop: { xs: "2rem", md: "0rem" },
            marginBottom: "0.5rem",
            fontStyle: "italic",
          }}
        >
          Description
        </Typography>
      </Box>
    );

    renderData.push(
      <Box sx={{ marginBottom: "1rem" }}>
        <Typography
          variant={"shirtViewText"}
          sx={{
            fontSize: "1rem !important",
            fontWeight: "400 !important",
          }}
        >
          {shirt.description}
        </Typography>
      </Box>
    );

    renderData.push(
      <Box>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: { xs: "1.2rem !important", md: "1.2rem !important" },
            marginBottom: "0.5rem",
            fontStyle: "italic",
          }}
        >
          Details
        </Typography>
      </Box>
    );
    /* renderData.push(<SimpleDataRender label={labels["purchase_date"]} value={showDateFormat(shirt.purchase_date)} />) */
    if (ability.can(Actions.VIEW_PURCHASING_VALUE, shirt)) {
      renderData.push(
        <SimpleDataRender
          label={labels["purchase_price"]}
          value={purchaseCostFormat(shirt.purchase_price)}
        />
      );
    }

    renderData = [...renderData, ...RenderShirtModel(shirt.model)];
    renderData.push(RenderProperties(shirt.properties));
    renderData.push(<SimpleDataRender label={"Size"} value={shirt.size} />);
    renderData.push(
      <SimpleDataRender
        label={labels["certificate"]}
        value={booleanFormat(shirt.certificate)}
      />
    );
    renderData.push(RenderCondition(shirt.condition));
    renderData.push(RenderVariant(shirt.model.variant));
    renderData.push(RenderSignature(shirt));

    return renderData;
  };

  return {
    RenderShirtType,
    RenderBrand,
    RenderCompetition,
    RenderPlayer,
    RenderSeason,
    RenderTeam,
    RenderVariant,
    RenderCondition,
    RenderShirtModel,
    RenderSignature,
    RenderShirt,
  };
}
